import {
  resolveAssistantRouteFamily,
  type AssistantRouteFamilyId,
} from "../config/ai-assistant/route-family-registry";
import {
  resolveAdmissionsDashboardArtifactSupport,
  type AdmissionsDashboardArtifactSupport,
} from "./admissions-dashboard-artifacts";
import { isTuitionPriorYearVisualizationPrompt } from "./assistant-visualization-prompts";

export type AssistantArtifactWorkflowKey =
  | "artifact.spreadsheet.generate"
  | "artifact.chart.generate"
  | "artifact.visualization3d.generate";

export type AssistantArtifactSupportMode =
  | "direct"
  | "grounded"
  | "unsupported";

export type AssistantArtifactSupport = {
  supported: boolean;
  fallbackToChat: boolean;
  mode: AssistantArtifactSupportMode;
  reason?: string;
  sourceId?:
    | AdmissionsDashboardArtifactSupport["sourceId"]
    | "tuition_dashboard_live";
  chartRequest?: AdmissionsDashboardArtifactSupport["chartRequest"];
};

const GENERIC_ARTIFACT_ROUTE_FAMILIES = new Set<AssistantRouteFamilyId>([
  "financial-aid",
  "settings",
  "students-families",
  "faculty",
  "tuition",
]);

const UNSUPPORTED_GENERIC_CHART_PATTERN =
  /\b(scatter|bubble|radar|pie|donut|heatmap)\b/i;

const directSupportToArtifactSupport = (
  support: AdmissionsDashboardArtifactSupport,
): AssistantArtifactSupport => ({
  supported: support.supported,
  fallbackToChat: support.fallbackToChat,
  mode: support.supported ? "direct" : "unsupported",
  reason: support.reason,
  sourceId: support.sourceId,
  chartRequest: support.chartRequest,
});

const supportsGenericArtifactRoute = (pathname: string): boolean =>
  GENERIC_ARTIFACT_ROUTE_FAMILIES.has(resolveAssistantRouteFamily(pathname).id);

const resolveGenericArtifactSupport = (
  workflowKey: AssistantArtifactWorkflowKey,
  question: string,
): AssistantArtifactSupport => {
  if (workflowKey === "artifact.visualization3d.generate") {
    if (isTuitionPriorYearVisualizationPrompt(question)) {
      return {
        supported: true,
        fallbackToChat: false,
        mode: "direct",
        sourceId: "tuition_dashboard_live",
        reason:
          "Generate a tuition payment 3D visualization from the authorized tuition dashboard datasource.",
      };
    }
    return {
      supported: false,
      fallbackToChat: true,
      mode: "unsupported",
      reason:
        "Three.js admissions visualizations are supported only by the direct admissions dashboard artifact lane.",
    };
  }

  if (
    workflowKey === "artifact.chart.generate" &&
    UNSUPPORTED_GENERIC_CHART_PATTERN.test(question)
  ) {
    return {
      supported: false,
      fallbackToChat: true,
      mode: "unsupported",
      reason:
        "Supported generic chart tasks are limited to bar, stacked-bar, line, and area charts.",
    };
  }

  return {
    supported: true,
    fallbackToChat: false,
    mode: "grounded",
    reason:
      workflowKey === "artifact.chart.generate"
        ? "Generate a grounded chart artifact from the current route context and citations."
        : "Generate a grounded spreadsheet artifact from the current route context and citations.",
  };
};

export const resolveAssistantArtifactSupport = (input: {
  pathname: string;
  workflowKey: AssistantArtifactWorkflowKey;
  question: string;
}): AssistantArtifactSupport => {
  const routeFamily = resolveAssistantRouteFamily(input.pathname).id;
  const directSupport = resolveAdmissionsDashboardArtifactSupport({
    pathname: input.pathname,
    workflowKey: input.workflowKey,
    question: input.question,
  });
  if (directSupport.supported) {
    return directSupportToArtifactSupport(directSupport);
  }

  if (routeFamily === "assistant-home") {
    const support = resolveGenericArtifactSupport(input.workflowKey, input.question);
    return {
      ...support,
      mode: support.supported ? "grounded" : support.mode,
      reason: support.supported
        ? "Start the artifact task from Assistant Home and let the workflow resolver bind a concrete identity-authorized source route."
        : support.reason,
    };
  }

  if (routeFamily === "tuition") {
    if (input.workflowKey === "artifact.spreadsheet.generate") {
      return {
        supported: true,
        fallbackToChat: false,
        mode: "direct",
        sourceId: "tuition_dashboard_live",
        reason:
          "Generate a grounded tuition workbook from the authorized tuition dashboard datasource.",
      };
    }
    if (
      input.workflowKey === "artifact.chart.generate" &&
      !UNSUPPORTED_GENERIC_CHART_PATTERN.test(input.question)
    ) {
      return {
        supported: true,
        fallbackToChat: false,
        mode: "direct",
        sourceId: "tuition_dashboard_live",
        reason:
          "Generate a grounded tuition collection chart from the authorized tuition dashboard datasource.",
      };
    }
    if (input.workflowKey === "artifact.visualization3d.generate") {
      if (isTuitionPriorYearVisualizationPrompt(input.question)) {
        return {
          supported: true,
          fallbackToChat: false,
          mode: "direct",
          sourceId: "tuition_dashboard_live",
          reason:
            "Generate a tuition payment 3D visualization from the authorized tuition dashboard datasource.",
        };
      }
      return {
        supported: false,
        fallbackToChat: true,
        mode: "unsupported",
        reason:
          "Three.js admissions visualizations are supported only on the admissions dashboard route aliases.",
      };
    }
  }

  if (!supportsGenericArtifactRoute(input.pathname)) {
    return {
      supported: false,
      fallbackToChat: true,
      mode: "unsupported",
      reason:
        directSupport.reason ??
        "Artifact generation is not available on this route.",
    };
  }

  return resolveGenericArtifactSupport(input.workflowKey, input.question);
};
